import * as React from 'react';

import { ErrorBoundary } from 'bernie-view';
import { LocalizedText } from 'bernie-l10n';

import { HotelMapModel, LocationSummaryViewModel, OverviewViewModel, WalkScoreViewModel } from 'models';

import { UitkCardTextSegment } from 'uitk-react-cards';
import { UitkIcon, UitkIconSize } from 'uitk-react-icons';

import { LocationSummary } from 'components/infosite/property-details/location-summary';
import { MapTrigger } from 'components/infosite/property-details/property-map/map-trigger';
import { NearbyList } from 'components/infosite/whats-around/internals/nearby-list';

export interface MapAndLocationDescriptionProps {
  hotelName?: string;
  locationSummary?: LocationSummaryViewModel;
  mapModel?: HotelMapModel;
  overview?: OverviewViewModel;
  isRefreshedHISLayout?: boolean;
  walkScoreModel?: WalkScoreViewModel;
}

export const MapAndLocationDescription: React.FunctionComponent<MapAndLocationDescriptionProps> = ({
  hotelName,
  locationSummary,
  mapModel,
  overview,
  isRefreshedHISLayout,
  walkScoreModel
}) => {
  const hasLocationSummary = Boolean(locationSummary && locationSummary.title && locationSummary.locations);

  return (
    <React.Fragment>
      {isRefreshedHISLayout && (
        <div className="uitk-grid all-y-padding-three all-x-gutter-six">
          {hasLocationSummary && (
            <>
              <div className="uitk-cell all-b-padding-two">
                <h2 className="uitk-type-500">{locationSummary.title}</h2>
              </div>
              <div className="uitk-cell s-cell-1-1 l-cell-1-2 xl-cell-1-2">
                <LocationSummary locations={locationSummary.locations} />
              </div>
            </>
          )}
          <HotelMap
            hotelName={hotelName}
            mapModel={mapModel}
            overview={overview}
            className="uitk-cell s-cell-1-1 l-cell-1-2 xl-cell-1-2 map-large"
          />
        </div>
      )}
      {!isRefreshedHISLayout && (
        <UitkCardTextSegment>
          <React.Fragment>
            <HotelMap hotelName={hotelName} mapModel={mapModel} overview={overview} className="uitk-grid all-cell-1-1" />
            {hasLocationSummary && (
              <React.Fragment>
                {locationSummary.title && (
                  <NearbyList outerClass="all-t-padding-three" title={locationSummary.title} walkScoreModel={walkScoreModel} />
                )}
                <LocationSummary locations={locationSummary.locations} />
              </React.Fragment>
            )}
          </React.Fragment>
        </UitkCardTextSegment>
      )}
    </React.Fragment>
  );
};

export interface HotelMapProps {
  hotelName: string;
  mapModel: HotelMapModel;
  overview: OverviewViewModel;
  className: string;
}

export const HotelMap: React.FunctionComponent<HotelMapProps> = ({ hotelName, mapModel, overview, className }) => {
  if (!(overview && overview.staticMapSrc)) {
    return null;
  }

  return mapModel ? (
    <div className={className}>
      <div className="uitk-cell">
        <h2 className="is-visually-hidden">
          <LocalizedText message="hotels.infosite.map.a11yLabel" />
        </h2>
        <ErrorBoundary label="hotel.information.details.map">
          <MapTrigger mapModel={mapModel} alt={hotelName} src={overview.staticMapSrc} className="uitk-cell" />
        </ErrorBoundary>
      </div>
      <div className="uitk-cell all-t-padding-two">
        <ErrorBoundary label="hotel.information.details.mapAddress">
          <MapTrigger mapModel={mapModel} alt={hotelName} overview={overview} />
        </ErrorBoundary>
      </div>
    </div>
  ) : (
    <div className="uitk-cell pwa-theme--grey-700">
      <div data-stid="content-hotel-address" itemProp="address" itemScope={true} itemType="http://schema.org/PostalAddress">
        <UitkIcon name="place" size={UitkIconSize.SMALL} />
        <span className="all-l-padding-one uitk-type-300 uitk-type-center">{overview.addressLine}</span>
        <meta itemProp="streetAddress" content={overview.addressLine} />
      </div>
    </div>
  );
};
